import React, { useState, useEffect, useMemo, useRef } from "react";
import UploadFilePopup from "../Popups/UploadFilePopup";
import Popup from "../Popups/Popup";
import Dropzone from 'react-dropzone'

import { Field } from "../Types/Form";
import useDataStore from "../Hooks/useDataStore";
import useSaveNewDocument from "../Hooks/useSaveNewDocument";
import useGetAllDocuments from "../Hooks/useGetAllDocuments";
import Input from "../FormUi/Input";
import Loading from "../Ui/Loading";
import Base64ToFile from "../Utils/Base64ToFile";

import { IDocumentModel } from "../Common/types";
import { CheckingStatuses, CheckingStatusesDescription } from "../enums/DocCheckingStatuses";
import { DocumentTypes, DocumentTypeTranslations } from "../enums/DocumentTypes";

import usePostGetDocuments from "../Api/usePostGetDocuments";

import { useAppSelector } from "../Hooks/hooks";
import { Provider } from "react-redux";
import { store } from "../Store/store";

type props = {
    updateDocuments: () => any;
    handleDocument: (document) => any;
    CanUploadFromAllDocs?: boolean;
    RequiredTypes?: DocumentTypes[];
    AllowFileTypes?: string[];
    MaxSizeFile: number;

    isLoading: boolean;
    isError: boolean;
    isSuccess: boolean;
    error: string;
}

const oneMb = 1048576;

const docTypesNum = Object.values(DocumentTypes).filter(value => typeof value === "number")

const EditUploadedDocumentPopup = ({ isLoading, isError, isSuccess, error, updateDocuments, handleDocument, AllowFileTypes, RequiredTypes, CanUploadFromAllDocs, MaxSizeFile }: props) => {
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const [head, setHead] = useState<{ title: string, description?: string }>({ title: "Загрузите файл" });
    const [actionButtons, setActionButtons] = useState(null);
    const [isFinally, setIsFinally] = useState(false);
    const [isChangeClose, setIsChangeClose] = useState(false);
    const [step, setStep] = useState(2);

    const [allowTypes, setAllowTypes] = useState(AllowFileTypes); //Расширение документа
    const [file, setFile] = useState(null);
    const fileRef = useRef(file);
    const [errorText, setErrorText] = useState(null);
    const [maxSizeB, setMaxSizeB] = useState(MaxSizeFile * 1048576);

    const setAllDocuments = () => {
        if (RequiredTypes) return useAppSelector(state => state.allDocuments.documents).filter((i: IDocumentModel) => RequiredTypes?.includes(i.DocumentTypeId))
        return useAppSelector(state => state.allDocuments.documents);
    }

    const allDocuments = setAllDocuments();

    useEffect(() => {
        if (CanUploadFromAllDocs) return setStep(1);
        if (AllowFileTypes && AllowFileTypes[0] === "*") return setAllowTypes(null);
    }, [])

    const setDocTypesList = () => {
        if (RequiredTypes) return RequiredTypes.map(item => ({ name: DocumentTypeTranslations[item], value: item }))
        return docTypesNum.map(item => ({ name: DocumentTypeTranslations[item], value: item }))
    }

    const [fields, setFields] = useState<Field[]>([
        {
            name: "DocumentTypeId", title: "Тип документа", element: "select", require: true, editable: true,
            options: setDocTypesList()
        },
        { name: "Name", title: "Название", require: true, editable: true },
        { name: "Comment", title: "Комментарий", editable: true }
    ])

    const { errors, handleChange, values, handleCancel, getData } = useDataStore({ fields: fields });

    useEffect(() => {
        if (isError) {
            setHead({ title: "Произошла ошибка..", description: error });
            setIsFinally(true);
        }
    }, [isError])

    useEffect(() => {
        if (isSuccess) {
            setHead({ title: "Документ успешно сохранён!" });
            setIsFinally(true);
        }
    }, [isSuccess])

    useEffect(() => {
        setLoading(isLoading);
    }, [isLoading])



    const handleFile = (file) => {
        setErrorText(null);
        if (file[0].size > maxSizeB) return setErrorText(`Размер файла не должен прeвышать ${maxSizeB / oneMb} МБ`);
        if (!file[0]?.type || allowTypes && !allowTypes.some(type => file[0].type.match(new RegExp(allowTypes.join("|"), "i")))) return setErrorText("Недопустимое расширение файла");

        setFields(prev => prev.map(f => f.name === "Name" ? { ...f, initialValue: file[0].name } : f));
        setFile(file[0]);
        fileRef.current = file[0];
        setStep(3);
    }

    const getEditableFile = (data) => {
        const saveDocumentData = { ...data, File: data.File || fileRef.current };
        handleDocument(saveDocumentData);
        setIsChangeClose(true);
    }

    useEffect(() => {
        if (step === 1) { setActionButtons(null) }
        if (step === 3) {
            setHead({ title: "Редактирование файла" });
            setActionButtons([
                { name: "Сохранить", action: () => getData(getEditableFile) },
                { name: "Отмена", action: () => reset() }
            ]);
        }
        if (step === 4) setActionButtons([{ name: "Назад", action: () => reset() }]);
    }, [step]);



    const step1 = () => (
        <>
            <button className="defaultPinkButton" onClick={() => setStep(2)}>Загрузить новый документ</button>
            <button className="defaultPinkButton" onClick={() => setStep(4)}>Добавить уже загруженный документ</button>
        </>
    )

    const step2 = () => (
        <div className="defaultDropZone">
            <Dropzone
                onDrop={handleFile}
                maxSize={maxSizeB}
            >
                {({ getRootProps, getInputProps }) => (
                    <div className="defaultDropZoneButton"
                        {...getRootProps()}
                    >
                        <input {...getInputProps()} />
                        <img src="../../main-images/dropZoneIcon.svg"></img>
                    </div>
                )}
            </Dropzone>

            <p className="dropZoneRules">
                Максимальный размер файла не более {maxSizeB / oneMb} Мб
            </p>
            {errorText &&
                <p className="errorText">{errorText}</p>
            }
        </div>
    )

    const step3 = () => (
        fields.map(field =>
            <Input
                field={field}
                value={values[field.name]}
                error={errors[field.name]}
                onChange={handleChange}
                onCancel={handleCancel}
            />
        )
    )

    const step4 = () => {
        const field = {
            name: "document",
            title: "Документ",
            element: "select",
            require: true,
            options: allDocuments.map(i => ({ name: i.Name, value: { ...i, File: Base64ToFile(i.DocumentBase64, i.Name) } }))
        }

        return (
            <Input
                field={field}
                onChange={(name, value) => { getEditableFile(value) }}
            />
        )
    }

    const renderBody = () => {
        if (step === 1) return step1();

        if (step === 2) return step2();

        if (step === 3) return step3();

        if (step === 4) return step4();
    }

    let onClose = () => {
        if (!isChangeClose) {
            setIsOpen(false);
            reset();
        } else {
            reset();
            setIsOpen(false);
            setIsChangeClose(false);
            updateDocuments();
        }
    }

    const reset = () => {
        setHead({ title: "Загрузите файл" });
        setIsFinally(false);
        setFile(null);
        fileRef.current = null;
        if (CanUploadFromAllDocs) {
            setStep(1);
        } else {
            setStep(2);
        }
    }

    return (
        <>
            <button onClick={() => setIsOpen(true)} className="openEditUploadedDocument">Добавить</button>

            <Popup
                isOpen={isOpen}
                head={head}
                close={onClose}
                loading={loading}
                body={renderBody()}
                actionButtons={actionButtons}
                isFinally={isFinally}
            />
        </>
    );
}

export default EditUploadedDocumentPopup;